/**
 * Sales Profile Service
 * Handles API calls to check if the current user has a sales unit assigned.
 * Follows the same pattern as manufacturing/profileService.ts and gaushala/profileService.ts
 */

import { getServiceUrl } from '@/config/serviceConfig';

const API_BASE_URL = getServiceUrl('commerce');

// ============================================================================
// CUSTOM ERROR CLASS
// ============================================================================

/**
 * Custom error class for sales-specific errors
 */
export class SalesError extends Error {
  constructor(
    message: string,
    public code: 'NO_UNIT_ASSIGNED' | 'API_ERROR' | 'UNKNOWN',
    public originalError?: Error
  ) {
    super(message);
    this.name = 'SalesError';
  }
}

// ============================================================================
// NO UNIT MESSAGES (for matching backend responses)
// ============================================================================

const NO_UNIT_MESSAGES = [
  'no sales unit assigned',
  'no sales unit',
];

// ============================================================================
// API METHODS
// ============================================================================

/**
 * Get the current user's sales unit profile.
 * Calls GET /api/v1/sales-access/my-sales-units which derives the unit from the JWT token.
 *
 * @returns Promise that resolves with sales unit data if user has units assigned
 * @throws SalesError with code NO_UNIT_ASSIGNED if no unit is assigned
 */
export const getSalesProfile = async (): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/sales-access/my-sales-units`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Sales Profile API Error Response:', {
        status: response.status,
        body: errorData,
      });

      // Check if the error is specifically due to no sales unit being assigned.
      // The backend returns 403 with {status: "NO_ASSIGNMENT", ...} when a SALES_TEAM
      // user has no units, but returns 500 (uncaught AccessDeniedException) when a
      // user of a different role hits the endpoint. Treat both as "no access" because
      // the result is the same — the user has no sales unit to operate on.
      const errorMessage = (errorData?.message || errorData?.error || '').toLowerCase();
      const errorStatus = (errorData?.status || '').toString().toUpperCase();

      const isNoAssignment =
        errorStatus === 'NO_ASSIGNMENT' ||
        (errorData?.success === false && errorData?.module === 'Sales Unit') ||
        response.status === 403 ||
        response.status === 500 ||
        NO_UNIT_MESSAGES.some(msg => errorMessage.includes(msg));

      if (isNoAssignment) {
        throw new SalesError(
          'No sales unit is assigned to your account. Please contact your administrator.',
          'NO_UNIT_ASSIGNED'
        );
      }

      throw new SalesError(
        errorData?.message || `API Error: ${response.status} ${response.statusText}`,
        'API_ERROR'
      );
    }

    const result = await response.json();
    console.log('Sales Profile API Response:', result);

    // Check if salesUnitIds is empty
    const salesUnitIds = result?.salesUnitIds || [];
    if (salesUnitIds.length === 0) {
      throw new SalesError(
        'No sales unit is assigned to your account. Please contact your administrator.',
        'NO_UNIT_ASSIGNED'
      );
    }

    return result;
  } catch (error) {
    console.error('Error fetching sales profile:', error);

    // Re-throw SalesError as is
    if (error instanceof SalesError) {
      throw error;
    }

    // Wrap other errors
    throw new SalesError(
      error instanceof Error ? error.message : 'Failed to load sales profile',
      'UNKNOWN',
      error instanceof Error ? error : undefined
    );
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getAuthToken = (): string => {
  return localStorage.getItem('saubhagya_jwt_token') || '';
};
